import type { DailyCall, DailyInputSettings } from "@daily-co/daily-js"

// Replacing an actively published microphone can race Daily's asynchronous RTP publication.
// Configure noise suppression only while muted, and keep unmute/room capture behind the update.
const pendingUpdates = new WeakMap<DailyCall, Promise<unknown>>()

export const isNativeNoiseReductionPending = (call: DailyCall | null | undefined) => !!call && pendingUpdates.has(call)

export const waitForNativeNoiseReduction = async (call: DailyCall) => {
  try { await pendingUpdates.get(call) } catch { /* The settings control reports the error. */ }
}

export const isMicrophoneActive = (audio?: boolean, state?: string) =>
  !!audio || state === "loading" || state === "sendable" || state === "playable"

export const updateNativeNoiseReduction = (
  call: DailyCall,
  inputSettings: DailyInputSettings | null | undefined,
  enabled: boolean,
  update: (settings: DailyInputSettings) => Promise<unknown>,
) => {
  if (pendingUpdates.has(call)) return pendingUpdates.get(call)!
  const pending = Promise.resolve().then(() => {
    const local = call.participants()?.local
    if (call.isDestroyed() || isMicrophoneActive(call.localAudio(), local?.tracks?.audio?.state)) {
      throw new Error("Mute your microphone to change noise reduction.")
    }
    return update(nativeNoiseReductionSettings(inputSettings, enabled))
  }).finally(() => {
    if (pendingUpdates.get(call) === pending) pendingUpdates.delete(call)
  })
  pendingUpdates.set(call, pending)
  return pending
}

/**
 * Turns browser noise reduction on for a new call unless the attendee (or a room move) already
 * chose a setting. Runs while the microphone is muted; failures leave the browser default.
 */
export const enableDefaultNativeNoiseReduction = async (call: DailyCall) => {
  if (!supportsNativeNoiseReduction()) return
  try {
    const inputSettings = await call.getInputSettings()
    const settings = inputSettings?.audio?.settings
    if (hasCustomAudioTrack(inputSettings) || (settings && "noiseSuppression" in settings && settings.noiseSuppression !== undefined)) return
    await updateNativeNoiseReduction(call, inputSettings, true, (next) => call.updateInputSettings(next))
  } catch {
    // Unsupported constraint, a device error, or the attendee unmuted first: keep the browser default.
  }
}

export const supportsNativeNoiseReduction = () =>
  typeof navigator !== "undefined" && navigator.mediaDevices?.getSupportedConstraints?.().noiseSuppression === true

/** Safari (macOS and iOS) has no noiseSuppression constraint; its voice processing is always on instead. */
export const isSafariBrowser = () =>
  typeof navigator !== "undefined" && /safari/i.test(navigator.userAgent) && !/chrome|chromium|crios|fxios|edg|android/i.test(navigator.userAgent)

export const describeUnsupportedNoiseReduction = () =>
  isSafariBrowser()
    ? "Safari reduces background noise with its own voice processing, so there is no separate switch. On a Mac, Control Center > Mic Mode > Voice Isolation filters more."
    : "Noise reduction is unavailable for this browser or microphone."

export const hasCustomAudioTrack = (inputSettings?: DailyInputSettings | null) =>
  !!inputSettings?.audio?.settings && "customTrack" in inputSettings.audio.settings

export const readNativeNoiseReduction = (inputSettings?: DailyInputSettings | null, track?: MediaStreamTrack | null) => {
  const actual = track?.readyState !== "ended" ? track?.getSettings?.().noiseSuppression : undefined
  if (typeof actual === "boolean") return actual
  const settings = inputSettings?.audio?.settings
  if (!settings || "customTrack" in settings) return false
  const configured = settings.noiseSuppression
  return typeof configured === "boolean" ? configured : configured?.exact ?? configured?.ideal ?? false
}

/** Daily replaces the whole constraints object, so retain device and other audio constraints. */
export const nativeNoiseReductionSettings = (inputSettings: DailyInputSettings | null | undefined, enabled: boolean): DailyInputSettings => {
  if (hasCustomAudioTrack(inputSettings)) throw new Error("Noise reduction is unavailable for a custom audio track.")
  return { audio: { processor: { type: "none" }, settings: { ...inputSettings?.audio?.settings, noiseSuppression: enabled } } }
}
